import {useEffect, useMemo, useState, type ReactNode} from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  AlertCircle,
  Play,
  FlaskConical,
  ChevronDown,
  ChevronRight,
  Clock,
  UploadCloud,
  RefreshCw,
  Download,
  Search
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NativeSelect,
  NativeSelectOption,
  Spinner,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {type ScrapyCollection, type TestRunWindow} from '@/api/scrapyCollections';
import {getApiErrorMessage} from '@/api/client';
import {useConfirm} from '@/components/shared/useConfirm';
import {ImportScrapyCollectionsDialog} from '@/components/admin/ImportScrapyCollectionsDialog';
import {ItemsOverviewChart} from '@/components/admin/ItemsOverviewChart';
import {RunItemsModal} from '@/components/admin/RunItemsModal';
import {CollectionHistoryPanel} from '@/components/admin/CollectionHistoryPanel';
import {exportCollectionsCsv} from '@/lib/collectionCsv';
import {
  useScrapyCollections,
  useScrapyCollectionRuns,
  useCreateScrapyCollection,
  useUpdateScrapyCollection,
  useDeleteScrapyCollection,
  useRunScrapyCollection,
  useTestRunScrapyCollection,
  useScrapyCollectionJob,
  useScrapyCollectionAllItems,
  useClassificationConfig
} from '@/api/hooks';
import {cn} from '@/lib/utils';
import {getTermMatches, itemSearchText, parseSearchQuery} from '@/lib/searchQuery';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {ScheduleInput} from '@/components/shared/ScheduleInput';
import {LanguageSelect} from '@/components/shared/LanguageSelect';
import {isPolicyViolationType} from '@/lib/classificationType';
import {DEFAULT_AFFILIATE, DEFAULT_SPIDER_NAME} from '@/lib/platform';
import {TablePanel} from '@/components/ui/table-panel';
import {showToast} from '@/lib/toast';
import {useDebounce} from '@/lib/useDebounce';
import {PageHeaderActions} from '@/components/layout/PageHeader';
import {jobStatusLabel, jobStatusTone, toneBadge} from '@/lib/status';

// TO DO UPDATE THIS ONCE WE ADD MORE CUSTOMER OPTIONS.
const CUSTOMER_OPTIONS = [DEFAULT_AFFILIATE];

type TestWindowPreset = '15' | '30' | '45' | '60' | '120' | 'custom';
const TEST_WINDOW_PRESETS: TestWindowPreset[] = ['15', '30', '45', '60', '120'];
const TEST_WINDOW_UNIT_LABEL: Record<TestRunWindow['window_unit'], string> = {
  min: 'minute',
  hour: 'hour',
  day: 'day'
};

function describeTestWindow(size: number, unit: TestRunWindow['window_unit']): string {
  const label = TEST_WINDOW_UNIT_LABEL[unit];
  return `${size} ${label}${size === 1 ? '' : 's'}`;
}

// Mirrors AdminScrapyCollectionsController.TestRunWindowCaps so a bad custom size fails in the
// input itself instead of round-tripping to the server first.
const TEST_WINDOW_CAPS: Record<TestRunWindow['window_unit'], number> = {
  min: 1440,
  hour: 168,
  day: 30
};

function clampTestWindowSize(size: number, unit: TestRunWindow['window_unit']): number {
  return Math.min(Math.max(Math.trunc(size) || 1, 1), TEST_WINDOW_CAPS[unit]);
}

type ArgRow = {id: string; key: string; value: string};

type FormState = {
  name: string;
  description: string;
  query: string;
  extraArgs: ArgRow[];
  cronExpression: string;
  isActive: boolean;
  affiliate: string;
  policyViolation: string;
  language: string;
};

function blankForm(): FormState {
  return {
    name: '',
    description: '',
    query: '',
    extraArgs: [],
    cronExpression: '',
    isActive: true,
    affiliate: '',
    policyViolation: '',
    language: ''
  };
}

function collectionToForm(c: ScrapyCollection): FormState {
  const {query = '', ...rest} = c.spider_args;
  return {
    name: c.name,
    description: c.description ?? '',
    query,
    extraArgs: Object.entries(rest).map(([key, value]) => ({
      id: globalThis.crypto.randomUUID(),
      key,
      value
    })),
    cronExpression: c.cron_expression ?? '',
    isActive: c.is_active,
    affiliate: c.affiliate ?? '',
    policyViolation: c.policy_violation ?? '',
    language: c.language ?? ''
  };
}

function formToSpiderArgs(f: FormState): Record<string, string> {
  const args: Record<string, string> = {query: f.query.trim()};
  for (const row of f.extraArgs) {
    if (row.key.trim()) {
      args[row.key.trim()] = row.value;
    }
  }

  return args;
}

function CollectionModal({
  initial,
  isEdit,
  onSave,
  onClose
}: {
  readonly initial: FormState;
  readonly isEdit: boolean;
  readonly onSave: (f: FormState) => Promise<void>;
  readonly onClose: () => void;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {data: classifications} = useClassificationConfig();
  const verticals = (classifications ?? []).filter((c) => isPolicyViolationType(c.type));

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((previous) => ({...previous, [k]: v}));
  }

  function updateArg(index: number, patch: Partial<ArgRow>) {
    setForm((previous) => ({
      ...previous,
      extraArgs: previous.extraArgs.map((row, i) => (i === index ? {...row, ...patch} : row))
    }));
  }

  function removeArg(index: number) {
    setForm((previous) => ({
      ...previous,
      extraArgs: previous.extraArgs.filter((_, i) => i !== index)
    }));
  }

  async function submit() {
    setError(null);
    setPending(true);
    try {
      await onSave(form);
      onClose();
    } catch (error_) {
      setError(getApiErrorMessage(error_, "Couldn't save the collection."));
    } finally {
      setPending(false);
    }
  }

  const canSave = form.name.trim() !== '' && form.query.trim() !== '' && !pending;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit Collection: ${initial.name}` : 'New Scrapy Collection'}
          </DialogTitle>
          <DialogDescription>
            A reusable youtube_search config — run on demand, test, or schedule on a cron.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="gap-4">
          <Field>
            <FieldLabel htmlFor="collection-name">Name</FieldLabel>
            <Input
              id="collection-name"
              value={form.name}
              placeholder="e.g. Weekly election misinformation sweep"
              onChange={(e) => {
                set('name', e.target.value);
              }}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="collection-description">
              Description <span className="text-muted-foreground">(optional)</span>
            </FieldLabel>
            <Input
              id="collection-description"
              value={form.description}
              placeholder="What is this collection for?"
              onChange={(e) => {
                set('description', e.target.value);
              }}
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field>
              <FieldLabel htmlFor="collection-affiliate">
                Customer <span className="text-muted-foreground">(optional)</span>
              </FieldLabel>
              <NativeSelect
                id="collection-affiliate"
                wrapperClassName="w-full"
                value={form.affiliate}
                onChange={(e) => {
                  set('affiliate', e.target.value);
                }}>
                <NativeSelectOption value="">Unset (uses default)</NativeSelectOption>
                {CUSTOMER_OPTIONS.map((a) => (
                  <NativeSelectOption key={a} value={a}>
                    {a}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor="collection-policy-violation">
                Policy Violation <span className="text-muted-foreground">(optional)</span>
              </FieldLabel>
              <NativeSelect
                id="collection-policy-violation"
                wrapperClassName="w-full"
                value={form.policyViolation}
                onChange={(e) => {
                  set('policyViolation', e.target.value);
                }}>
                <NativeSelectOption value="">Unset</NativeSelectOption>
                {verticals.map((v) => (
                  <NativeSelectOption key={v.id} value={v.name}>
                    {v.displayName}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel>
                Language <span className="text-muted-foreground">(optional)</span>
              </FieldLabel>
              <LanguageSelect
                value={form.language}
                onChange={(name) => {
                  set('language', name);
                }}
              />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="collection-query">
              Query <span className="text-destructive-ink">(required)</span>
            </FieldLabel>
            <Input
              id="collection-query"
              value={form.query}
              placeholder="Search query passed to youtube_search"
              className="font-mono"
              onChange={(e) => {
                set('query', e.target.value);
              }}
            />
          </Field>

          <Field>
            <div className="flex items-center justify-between">
              <FieldLabel>
                Additional spider_args <span className="text-muted-foreground">(optional)</span>
              </FieldLabel>
              <Button
                type="button"
                variant="secondary"
                size="xs"
                onClick={() => {
                  set('extraArgs', [
                    ...form.extraArgs,
                    {id: globalThis.crypto.randomUUID(), key: '', value: ''}
                  ]);
                }}>
                <Plus /> Add row
              </Button>
            </div>
            {form.extraArgs.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                No extra args — youtube_search fields like search_type, days_limit, page_limit etc.
                can be added here.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {form.extraArgs.map((row, i) => (
                  <div key={row.id} className="flex items-center gap-1.5">
                    <Input
                      value={row.key}
                      aria-label="Spider argument key"
                      placeholder="key (e.g. days_limit)"
                      className="w-1/2 font-mono"
                      onChange={(e) => {
                        updateArg(i, {key: e.target.value});
                      }}
                    />
                    <Input
                      value={row.value}
                      aria-label="Spider argument value"
                      placeholder="value"
                      className="w-1/2 font-mono"
                      onChange={(e) => {
                        updateArg(i, {value: e.target.value});
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remove argument"
                      onClick={() => {
                        removeArg(i);
                      }}>
                      <Trash2 />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel>Schedule</FieldLabel>
            <ScheduleInput
              allowOnDemand
              compact
              value={form.cronExpression === '' ? null : form.cronExpression}
              onChange={(cron) => {
                set('cronExpression', cron ?? '');
              }}
            />
          </Field>

          <Field orientation="horizontal">
            <FieldLabel htmlFor="collection-active">Active</FieldLabel>
            <Switch
              id="collection-active"
              checked={form.isActive}
              onCheckedChange={() => {
                set('isActive', !form.isActive);
              }}
            />
          </Field>

          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </DialogBody>

        <DialogFooter sticky>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSave} onClick={() => void submit()}>
            {pending ? <Spinner /> : <Check />}
            {pending ? 'Saving...' : 'Save Collection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatRelativeTime(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) {
    return 'due now';
  }

  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) {
    return 'in <1m';
  }

  if (minutes < 60) {
    return `in ${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return remainingMinutes > 0 ? `in ${hours}h ${remainingMinutes}m` : `in ${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `in ${days}d ${remainingHours}h` : `in ${days}d`;
}

function downloadCollectionsCsv(collections: readonly ScrapyCollection[]): void {
  const csv = exportCollectionsCsv(collections);
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `scrapy-collections-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

const RUN_HISTORY_COLUMN_COUNT = 4;

function RunHistory({
  collectionId,
  query
}: {
  readonly collectionId: string;
  readonly query: string;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const {
    data: runs,
    isLoading,
    isFetching,
    refetch
  } = useScrapyCollectionRuns(collectionId, !collapsed, true);
  const [viewingRunId, setViewingRunId] = useState<string | null>(null);

  const rows = runs ?? [];

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex w-full items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2.5"
          aria-expanded={!collapsed}
          onClick={() => {
            setCollapsed((previous) => !previous);
          }}>
          {collapsed ? <ChevronRight /> : <ChevronDown />}
          <h4 className="text-foreground text-xs font-medium">Jobs</h4>
        </Button>
        {collapsed ? null : (
          <Button
            type="button"
            variant="outline"
            size="xs"
            disabled={isFetching}
            onClick={() => {
              void refetch();
            }}>
            {isFetching ? <Spinner /> : <RefreshCw />}
            Refresh
          </Button>
        )}
      </div>

      {collapsed ? null : isLoading ? (
        <p className="text-muted-foreground flex items-center gap-2 text-xs">
          <Spinner /> Loading run history…
        </p>
      ) : (
        <TablePanel>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Completed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={RUN_HISTORY_COLUMN_COUNT} className="p-0">
                    <Empty className="py-10">
                      <EmptyHeader>
                        <EmptyMedia variant="icon">
                          <Play />
                        </EmptyMedia>
                        <EmptyTitle>No runs yet</EmptyTitle>
                        <EmptyDescription>
                          This collection hasn&apos;t run yet. Use Run to trigger one.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  </TableCell>
                </TableRow>
              ) : null}
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  className={cn(r.item_count > 0 && 'hover:bg-muted/40 cursor-pointer')}
                  onClick={() => {
                    if (r.item_count > 0) {
                      setViewingRunId(r.id);
                    }
                  }}>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge {...toneBadge(jobStatusTone(r.status))}>
                        {jobStatusLabel(r.status)}
                      </Badge>
                      {r.is_test_run ? (
                        <Badge
                          variant="outline"
                          title="Bounded dry run (selectable time window) — not counted toward the collection's real run history">
                          Test
                        </Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {/* The row's onClick is pointer-only, so the count doubles as the focusable way
                        in; it stops propagation so the row handler doesn't open the modal twice. */}
                    {r.item_count > 0 ? (
                      <Button
                        type="button"
                        variant="link"
                        size="xs"
                        className="h-auto px-0 tabular-nums"
                        aria-label={`View ${r.item_count.toLocaleString()} items from this run`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setViewingRunId(r.id);
                        }}>
                        {r.item_count.toLocaleString()}
                      </Button>
                    ) : (
                      r.item_count.toLocaleString()
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TablePanel>
      )}
      {viewingRunId ? (
        <RunItemsModal
          jobId={viewingRunId}
          query={query}
          onClose={() => {
            setViewingRunId(null);
          }}
        />
      ) : null}
    </div>
  );
}

type TermStat = {readonly term: string; readonly negated: boolean; readonly matchedCount: number};

/**
 Which of the collection's query terms are actually pulling weight, across every item it has ever
 collected (every completed run, test runs included) - not just one run's page. Answers "which
 terms should I amend" directly: a term at 0% is dead weight, one dominating the count is worth
 tightening. Counts are computed client-side from already-fetched item text via the same
 parseSearchQuery/getTermMatches the run-items breakdown uses, so results stay consistent with the
 per-item badges elsewhere.

 Scoped to this exact collection row (`collection.id`), not its `CollectionGroupId` - an edit mints
 a new row with a new id (see ScrapyCollection.cs), and a run's Job.ScrapyCollectionId points at
 whichever row was current when it fired. So editing the query starts the Analysis fresh rather
 than blending in runs that matched different query wording - the right behaviour for "which of
 THIS query's terms are pulling weight", just worth knowing if the count looks lower than expected
 right after an edit.
 */
function CollectionAnalysisPanel({
  collectionId,
  query
}: {
  readonly collectionId: string;
  readonly query: string;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const runsQuery = useScrapyCollectionRuns(collectionId, !collapsed, true);
  const qualifyingJobIds = useMemo(
    () =>
      (runsQuery.data ?? [])
        // Test runs count too - a bounded dry run still crawled real results against the same
        // query, and its items are just as informative for "which terms are pulling weight".
        .filter((r) => r.status === 'completed' && r.item_count > 0)
        .map((r) => r.id),
    [runsQuery.data]
  );
  const itemsQuery = useScrapyCollectionAllItems(collectionId, qualifyingJobIds, !collapsed);

  const parsedQuery = useMemo(() => parseSearchQuery(query), [query]);
  const hasTerms = parsedQuery.include.length > 0 || parsedQuery.exclude.length > 0;

  const stats = useMemo(() => {
    if (!itemsQuery.data) {
      return null;
    }

    const counts = new Map<string, number>();
    let unmatchedCount = 0;
    for (const item of itemsQuery.data) {
      const matches = getTermMatches(itemSearchText(item), parsedQuery, true);
      const matchedTerms = [...matches.matchedInclude, ...matches.matchedExclude];
      if (matchedTerms.length === 0) {
        unmatchedCount += 1;
      }

      for (const term of matchedTerms) {
        counts.set(term, (counts.get(term) ?? 0) + 1);
      }
    }

    const terms: TermStat[] = [...parsedQuery.include, ...parsedQuery.exclude]
      .map((term) => ({
        term,
        negated: parsedQuery.exclude.includes(term),
        matchedCount: counts.get(term) ?? 0
      }))
      .sort((a, b) => b.matchedCount - a.matchedCount);

    return {terms, unmatchedCount, total: itemsQuery.data.length};
  }, [itemsQuery.data, parsedQuery]);

  const isLoading = runsQuery.isLoading || itemsQuery.isLoading;
  const isFetching = runsQuery.isFetching || itemsQuery.isFetching;

  let body: ReactNode = null;
  if (hasTerms) {
    if (isLoading) {
      body = (
        <p className="text-muted-foreground flex items-center gap-2 text-xs">
          <Spinner />
          Analyzing items across {qualifyingJobIds.length} run
          {qualifyingJobIds.length === 1 ? '' : 's'}…
        </p>
      );
    } else if (stats && stats.total > 0) {
      body = (
        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground text-xs">
            {stats.total.toLocaleString()} item{stats.total === 1 ? '' : 's'} analyzed across{' '}
            {qualifyingJobIds.length} run{qualifyingJobIds.length === 1 ? '' : 's'}.
            {stats.unmatchedCount > 0
              ? ` ${stats.unmatchedCount.toLocaleString()} (${Math.round((stats.unmatchedCount / stats.total) * 100)}%) matched none of the query's terms.`
              : ''}
          </p>
          <TablePanel>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Term</TableHead>
                  <TableHead className="text-right">Items matched</TableHead>
                  <TableHead className="text-right">% of collected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.terms.map((t) => (
                  <TableRow key={t.term}>
                    <TableCell>
                      <Badge variant={t.negated ? 'destructive' : 'secondary'}>
                        {t.negated ? '−' : ''}
                        {t.term}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {t.matchedCount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Math.round((t.matchedCount / stats.total) * 100)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TablePanel>
        </div>
      );
    } else {
      body = (
        <Empty variant="outline" className="gap-2 p-4">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Search />
            </EmptyMedia>
            <EmptyTitle>Nothing to analyze</EmptyTitle>
            <EmptyDescription>No completed run has collected any items yet.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      );
    }
  } else {
    body = (
      <Empty variant="outline" className="gap-2 p-4">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Search />
          </EmptyMedia>
          <EmptyTitle>Nothing to analyze</EmptyTitle>
          <EmptyDescription>
            This collection&apos;s query has no keyword terms to analyze.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex w-full items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2.5"
          aria-expanded={!collapsed}
          onClick={() => {
            setCollapsed((previous) => !previous);
          }}>
          {collapsed ? <ChevronRight /> : <ChevronDown />}
          <h4 className="text-foreground text-xs font-medium">Analysis</h4>
        </Button>
        {collapsed ? null : (
          <Button
            type="button"
            variant="outline"
            size="xs"
            disabled={isFetching}
            onClick={() => {
              void runsQuery.refetch();
              void itemsQuery.refetch();
            }}>
            {isFetching ? <Spinner /> : <RefreshCw />}
            Refresh
          </Button>
        )}
      </div>

      {collapsed ? null : body}
    </div>
  );
}

function CollectionCard({
  collection,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleActive
}: {
  readonly collection: ScrapyCollection;
  readonly expanded: boolean;
  readonly onToggleExpand: () => void;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onToggleActive: () => Promise<void>;
}) {
  const runCollection = useRunScrapyCollection();
  const testRunCollection = useTestRunScrapyCollection();
  const [testError, setTestError] = useState<string | null>(null);
  const [testJobId, setTestJobId] = useState<string | null>(null);
  const [testWindowPreset, setTestWindowPreset] = useState<TestWindowPreset>('15');
  const [testCustomSize, setTestCustomSize] = useState('15');
  const [testCustomUnit, setTestCustomUnit] = useState<TestRunWindow['window_unit']>('min');
  const [togglePending, setTogglePending] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const testJob = useScrapyCollectionJob(collection.id, testJobId);

  // Re-render every 30s so the "next run in X" countdown stays fresh without a manual refresh.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const interval = globalThis.setInterval(() => {
      forceTick((n) => n + 1);
    }, 30_000);
    return () => {
      globalThis.clearInterval(interval);
    };
  }, []);

  async function handleRun() {
    try {
      await runCollection.mutateAsync(collection.id);
      showToast('Run triggered');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Run failed'), 'error');
    }
  }

  const testWindow: TestRunWindow =
    testWindowPreset === 'custom'
      ? {
          window_size: clampTestWindowSize(Number(testCustomSize), testCustomUnit),
          window_unit: testCustomUnit
        }
      : {window_size: Number(testWindowPreset), window_unit: 'min'};

  async function handleTestRun() {
    setTestError(null);
    setTestJobId(null);
    try {
      const result = await testRunCollection.mutateAsync({id: collection.id, window: testWindow});
      setTestJobId(result.job_id);
    } catch (error) {
      setTestError(getApiErrorMessage(error, "Couldn't start the test run."));
    }
  }

  async function handleToggleActive() {
    setToggleError(null);
    setTogglePending(true);
    try {
      await onToggleActive();
    } catch (error) {
      setToggleError(getApiErrorMessage(error, "Couldn't update the collection."));
    } finally {
      setTogglePending(false);
    }
  }

  const testRunFailed = testJob.data?.status === 'failed' || testJob.data?.status === 'timed_out';
  const testRunSettled = testJob.data?.status === 'completed' || testRunFailed;

  // A bordered panel on the page background rather than a Card: the test-run window and the
  // active switch are form controls, which don't sit on a card surface. An inactive collection is
  // named by a badge rather than dimmed, so its controls never read as disabled.
  return (
    <div className="border-border bg-card rounded-lg border">
      <div className="flex flex-col justify-between gap-4 p-4 sm:flex-row sm:items-center">
        <button
          type="button"
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={onToggleExpand}>
          {expanded ? (
            <ChevronDown className="text-muted-foreground size-4 shrink-0" />
          ) : (
            <ChevronRight className="text-muted-foreground size-4 shrink-0" />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-foreground truncate text-xs font-medium">{collection.name}</p>
            <div className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
              {collection.is_active ? null : <Badge variant="outline">Inactive</Badge>}
              <Badge variant="outline">{collection.spider_name}</Badge>
              <span className="font-mono">{collection.spider_args.query}</span>
              {collection.language ? <Badge variant="outline">{collection.language}</Badge> : null}
              {collection.cron_expression ? (
                <>
                  <Badge variant="outline" className="font-mono">
                    <Clock />
                    {collection.cron_expression}
                  </Badge>
                  {collection.is_active && collection.next_run_at ? (
                    <Badge variant="outline">
                      Next run {formatRelativeTime(collection.next_run_at)}
                    </Badge>
                  ) : null}
                </>
              ) : null}
              {collection.updated_by ? <span>edited by {collection.updated_by}</span> : null}
            </div>
          </div>
        </button>

        <div className="flex shrink-0 flex-wrap items-center gap-2 self-end sm:self-auto">
          {testError ? (
            <span className="text-destructive-ink animate-in fade-in max-w-[220px] truncate text-xs">
              {testError}
            </span>
          ) : null}
          {testJobId ? (
            <Badge
              variant={testRunFailed ? 'destructive' : 'outline'}
              className="animate-in fade-in">
              {testRunSettled ? (
                testJob.data?.status === 'completed' ? (
                  <Check />
                ) : (
                  <AlertCircle />
                )
              ) : (
                <Spinner />
              )}
              Test run: {testJob.data?.status ?? 'starting'}
              {testJob.data
                ? ` — ${testJob.data.item_count} item${testJob.data.item_count === 1 ? '' : 's'}`
                : ''}
            </Badge>
          ) : null}

          <NativeSelect
            size="sm"
            value={testWindowPreset}
            disabled={testRunCollection.isPending}
            title="Test run window"
            aria-label="Test run window"
            onChange={(e) => {
              setTestWindowPreset(e.target.value as TestWindowPreset);
            }}>
            {TEST_WINDOW_PRESETS.map((preset) => (
              <NativeSelectOption key={preset} value={preset}>
                {preset} min
              </NativeSelectOption>
            ))}
            <NativeSelectOption value="custom">Custom…</NativeSelectOption>
          </NativeSelect>

          {testWindowPreset === 'custom' ? (
            <>
              <Input
                type="number"
                min={1}
                max={TEST_WINDOW_CAPS[testCustomUnit]}
                value={testCustomSize}
                aria-label="Test run window size"
                disabled={testRunCollection.isPending}
                className="h-7 w-16"
                onChange={(e) => {
                  setTestCustomSize(e.target.value);
                }}
              />
              <NativeSelect
                size="sm"
                value={testCustomUnit}
                aria-label="Test run window unit"
                disabled={testRunCollection.isPending}
                onChange={(e) => {
                  setTestCustomUnit(e.target.value as TestRunWindow['window_unit']);
                }}>
                <NativeSelectOption value="min">minutes</NativeSelectOption>
                <NativeSelectOption value="hour">hours</NativeSelectOption>
                <NativeSelectOption value="day">days</NativeSelectOption>
              </NativeSelect>
            </>
          ) : null}

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={testRunCollection.isPending}
                  onClick={() => void handleTestRun()}
                />
              }>
              {testRunCollection.isPending ? <Spinner /> : <FlaskConical />}
              Test run
            </TooltipTrigger>
            <TooltipContent>
              Bounded {describeTestWindow(testWindow.window_size, testWindow.window_unit)} test run
            </TooltipContent>
          </Tooltip>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={runCollection.isPending}
            onClick={() => void handleRun()}>
            {runCollection.isPending ? <Spinner /> : <Play className="fill-current" />}
            Run
          </Button>

          {toggleError ? (
            <span className="text-destructive-ink animate-in fade-in max-w-[220px] truncate text-xs">
              {toggleError}
            </span>
          ) : null}
          <Switch
            aria-label={collection.is_active ? 'Deactivate collection' : 'Activate collection'}
            checked={collection.is_active}
            disabled={togglePending}
            onCheckedChange={() => void handleToggleActive()}
          />

          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Edit collection"
            onClick={onEdit}>
            <Pencil />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Delete collection"
            className="hover:text-destructive-ink"
            onClick={onDelete}>
            <Trash2 />
          </Button>
        </div>
      </div>

      {expanded ? (
        <div className="border-border divide-border divide-y border-t">
          <RunHistory collectionId={collection.id} query={collection.spider_args.query ?? ''} />
          <CollectionAnalysisPanel
            collectionId={collection.id}
            query={collection.spider_args.query ?? ''}
          />
          <CollectionHistoryPanel collectionId={collection.id} />
        </div>
      ) : null}
    </div>
  );
}

export function ScrapyCollectionsAdmin() {
  const {data, isLoading} = useScrapyCollections();
  const createCollection = useCreateScrapyCollection();
  const updateCollection = useUpdateScrapyCollection();
  const deleteCollection = useDeleteScrapyCollection();
  const [modal, setModal] = useState<'create' | ScrapyCollection | null>(null);
  const [showImport, setShowImport] = useState(false);
  const {confirm, confirmDialog} = useConfirm();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [affiliateFilter, setAffiliateFilter] = useState('all');
  const [verticalFilter, setVerticalFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Filters the list and redraws the chart, so it waits for typing to pause.
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const {data: classifications} = useClassificationConfig();
  const verticals = (classifications ?? []).filter((c) => isPolicyViolationType(c.type));

  async function handleSave(form: FormState) {
    const spiderArgs = formToSpiderArgs(form);
    if (modal === 'create') {
      await createCollection.mutateAsync({
        name: form.name.trim(),
        description: form.description.trim() || null,
        spider_name: DEFAULT_SPIDER_NAME,
        spider_args: spiderArgs,
        cron_expression: form.cronExpression.trim() || null,
        is_active: form.isActive,
        affiliate: form.affiliate.trim() || null,
        policy_violation: form.policyViolation.trim() || null,
        language: form.language.trim() || null
      });
    } else if (modal) {
      const trimmedCron = form.cronExpression.trim();
      const trimmedAffiliate = form.affiliate.trim();
      const trimmedPolicyViolation = form.policyViolation.trim();
      const trimmedLanguage = form.language.trim();
      const editedId = modal.id;
      const updated = await updateCollection.mutateAsync({
        id: editedId,
        body: {
          name: form.name.trim(),
          description: form.description.trim() || null,
          spider_args: spiderArgs,
          ...(trimmedCron ? {cron_expression: trimmedCron} : {clear_cron_expression: true}),
          is_active: form.isActive,
          ...(trimmedAffiliate ? {affiliate: trimmedAffiliate} : {clear_affiliate: true}),
          ...(trimmedPolicyViolation
            ? {policy_violation: trimmedPolicyViolation}
            : {clear_policy_violation: true}),
          ...(trimmedLanguage ? {language: trimmedLanguage} : {clear_language: true})
        }
      });

      // The edit superseded the row with a new id — keep an expanded card expanded under it.
      setExpandedId((previous) => (previous === editedId ? updated.id : previous));
    }
  }

  async function handleDelete(collection: ScrapyCollection) {
    if (
      !(await confirm({
        title: `Delete "${collection.name}"?`,
        description: 'This action cannot be undone.'
      }))
    ) {
      return;
    }

    deleteCollection.mutate(collection.id, {
      onError(error) {
        showToast(getApiErrorMessage(error, 'Failed to delete collection.'), 'error');
      }
    });
  }

  async function handleToggleActive(collection: ScrapyCollection) {
    const updated = await updateCollection.mutateAsync({
      id: collection.id,
      body: {is_active: !collection.is_active}
    });
    setExpandedId((previous) => (previous === collection.id ? updated.id : previous));
  }

  const collections = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );

  const affiliateOptions = useMemo(
    () =>
      [
        ...new Set(
          collections.map((c) => c.affiliate).filter((a): a is string => a !== null && a !== '')
        )
      ].sort(),
    [collections]
  );

  const languageOptions = useMemo(
    () =>
      [
        ...new Set(
          collections.map((c) => c.language).filter((l): l is string => l !== null && l !== '')
        )
      ].sort(),
    [collections]
  );

  const filteredCollections = useMemo(() => {
    const needle = debouncedSearchQuery.trim().toLowerCase();
    return collections.filter(
      (c) =>
        (affiliateFilter === 'all' || c.affiliate === affiliateFilter) &&
        (verticalFilter === 'all' || c.policy_violation === verticalFilter) &&
        (languageFilter === 'all' || c.language === languageFilter) &&
        (!needle ||
          c.name.toLowerCase().includes(needle) ||
          (c.description ?? '').toLowerCase().includes(needle) ||
          (c.spider_args.query ?? '').toLowerCase().includes(needle))
    );
  }, [collections, affiliateFilter, verticalFilter, languageFilter, debouncedSearchQuery]);

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-6 text-xs">
        <Spinner /> Loading…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="tabular-nums">
            {filteredCollections.length} Collections
          </Badge>
          <InputGroup className="w-56">
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              placeholder="Search collections…"
              aria-label="Search collections"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
            />
          </InputGroup>
          <NativeSelect
            value={affiliateFilter}
            aria-label="Filter by customer"
            onChange={(e) => {
              setAffiliateFilter(e.target.value);
            }}>
            <NativeSelectOption value="all">All customers</NativeSelectOption>
            {affiliateOptions.map((a) => (
              <NativeSelectOption key={a} value={a}>
                {a}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            value={verticalFilter}
            aria-label="Filter by vertical"
            onChange={(e) => {
              setVerticalFilter(e.target.value);
            }}>
            <NativeSelectOption value="all">All verticals</NativeSelectOption>
            {verticals.map((v) => (
              <NativeSelectOption key={v.id} value={v.name}>
                {v.displayName}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            value={languageFilter}
            aria-label="Filter by language"
            onChange={(e) => {
              setLanguageFilter(e.target.value);
            }}>
            <NativeSelectOption value="all">All languages</NativeSelectOption>
            {languageOptions.map((l) => (
              <NativeSelectOption key={l} value={l}>
                {l}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <PageHeaderActions>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={collections.length === 0}
            onClick={() => {
              downloadCollectionsCsv(collections);
            }}>
            <Download />
            Export CSV
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowImport(true);
            }}>
            <UploadCloud />
            Import CSV
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setModal('create');
            }}>
            <Plus />
            New Collection
          </Button>
        </PageHeaderActions>
      </div>

      {/* Below the filter row it controls, so it reads as "these filters shape this chart". */}
      <ItemsOverviewChart
        affiliateFilter={affiliateFilter}
        verticalFilter={verticalFilter}
        languageFilter={languageFilter}
        searchQuery={debouncedSearchQuery}
      />

      {collections.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FlaskConical />
            </EmptyMedia>
            <EmptyTitle>No Scrapy Collections</EmptyTitle>
            <EmptyDescription>
              Click the button above to define a reusable youtube_search config.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : filteredCollections.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Search />
            </EmptyMedia>
            <EmptyTitle>No matches</EmptyTitle>
            <EmptyDescription>No collections match the selected filters.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredCollections.map((c) => (
            <CollectionCard
              key={c.id}
              collection={c}
              expanded={expandedId === c.id}
              onToggleExpand={() => {
                setExpandedId((previous) => (previous === c.id ? null : c.id));
              }}
              onEdit={() => {
                setModal(c);
              }}
              onDelete={() => {
                void handleDelete(c);
              }}
              onToggleActive={async () => handleToggleActive(c)}
            />
          ))}
        </div>
      )}

      {modal ? (
        <CollectionModal
          initial={modal === 'create' ? blankForm() : collectionToForm(modal)}
          isEdit={modal !== 'create'}
          onSave={handleSave}
          onClose={() => {
            setModal(null);
          }}
        />
      ) : null}

      {showImport ? (
        <ImportScrapyCollectionsDialog
          onClose={() => {
            setShowImport(false);
          }}
        />
      ) : null}
      {confirmDialog}
    </div>
  );
}
